import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Label,
} from '@repo/shadcn';
import { useDebounce } from '@uidotdev/usehooks';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PiPlusCircleDuotone } from 'react-icons/pi';

import { AutoCompleteInput } from '../../../../components/ui/autocomplete/auto-complete-input';
import { MODAL_ROUTE } from '../../../../constants';
import { useGetStrengthQuery } from '../../../../features/slice/products/strength/strength-api-slice';
import { useModal } from '../../../../hooks';
import { TProductForm } from './product-form.component';

export const StrengthSelector: FC = () => {
    const [searchInputText, setSearchInputText] = useState('');
    const { openModal } = useModal();

    const { setValue, watch, control } = useFormContext<TProductForm>();

    const selectedStrengthId = watch('strength_id');
    const selectedStrengthName = watch('strength_name');

    const debouncedSearchInputText = useDebounce(searchInputText, 500);

    const { data, isLoading, isFetching } = useGetStrengthQuery({
        search: debouncedSearchInputText,
    });

    const handleInputChange = (value: string) => {
        setSearchInputText(value);
    };

    const handleValueChange = (value: any) => {
        if (!value) {
            setValue('strength_id', '');
            setValue('strength_name', '');
            return;
        }

        setValue('strength_id', value.value);
        setValue('strength_name', value.label);
    };

    const generateOptions = () => {
        const options =
            data?.data.map((strength: any) => ({
                label: strength.name,
                value: strength.id,
            })) || [];

        // add selected option if not in the list
        if (
            selectedStrengthId &&
            selectedStrengthName &&
            !options.find((opt: any) => opt.value === selectedStrengthId)
        ) {
            options.unshift({
                label: selectedStrengthName,
                value: selectedStrengthId,
            });
        }

        return options;
    };

    const options = generateOptions();

    const selectedOption = {
        label: selectedStrengthName || '',
        value: selectedStrengthId || '',
    };

    return (
        <FormField
            control={control}
            name={'strength_id'}
            render={() => (
                <FormItem>
                    <FormLabel className="flex items-center justify-between">
                        <div>
                            <span>Strength</span>
                            <span className="ml-1 text-red-500">*</span>
                        </div>

                        <Label
                            onClick={() =>
                                openModal(MODAL_ROUTE.CREATE_PRODUCT_STRENGTH)
                            }
                            className="flex cursor-pointer items-center gap-1 text-primary hover:underline"
                        >
                            <PiPlusCircleDuotone size={16} className="-mt-1" />
                            Add Strength
                        </Label>
                    </FormLabel>

                    <FormControl>
                        <AutoCompleteInput
                            isLoading={isLoading || isFetching}
                            options={options}
                            value={selectedOption}
                            emptyMessage={'No items found'}
                            onValueChange={handleValueChange}
                            onInputChange={handleInputChange}
                            showSuggestionsOnFocus={true}
                        />
                    </FormControl>
                    <FormMessage className="line-clamp-1 text-xs" />
                </FormItem>
            )}
        />
    );
};

StrengthSelector.displayName = 'StrengthSelector';
